/** @format */

import {forwardRef, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {useOptionallyControlledState} from '../hooks/use_optionally_controlled_state';

import NumberFormat from 'react-number-format';

export const Autocomplete = forwardRef(
    (
        {
            type,
            label,
            value,
            onChange,
            name,
            data,
            filter,
            disabled,
            error,
            className,
            suffix,
            suggestedText,
            noResultsText,
            displayFn,
            size,
            ...props
        },
        ref
    ) => {
        const hasError = !!error;
        const fieldClass = classNames('field', hasError && 'field--error');
        const inputClass = classNames('control arrow-indicator', className, {'w-100': !size});

        const [visible, setVisible] = useState(false);

        const [state, setState] = useOptionallyControlledState(value, '', onChange);
        const results = filter(state, data);

        var Input;
        if (type === 'number') {
            Input = (
                <NumberFormat
                    ref={ref}
                    className={inputClass}
                    placeholder={label}
                    name={name}
                    suffix={suffix}
                    id={name}
                    disabled={disabled}
                    value={state}
                    aria-invalid={hasError}
                    onValueChange={(values, _) => setState(values.value)}
                    onFocus={() => setVisible(true)}
                    onBlur={() => {
                        setVisible(false);
                    }}
                    {...props}
                />
            );
        } else {
            Input = (
                <input
                    ref={ref}
                    className={inputClass}
                    placeholder={label}
                    name={name}
                    type={type}
                    id={name}
                    disabled={disabled}
                    value={state}
                    aria-invalid={hasError}
                    onChange={e => setState(e.target.value)}
                    onFocus={() => setVisible(true)}
                    onBlur={() => setVisible(false)}
                    data-testid="input"
                    {...props}
                />
            );
        }

        return (
            <div className={fieldClass}>
                {Input}
                <label className="floating-label" htmlFor={name}>
                    <span>{label}</span>
                </label>
                {visible && (
                    <AutocompleteResults
                        results={results}
                        onClick={setState}
                        suggestedText={suggestedText}
                        noResultsText={noResultsText}
                        displayFn={displayFn}
                    />
                )}
                {hasError && <p className="mt-1 color-alert-negative">{error}</p>}
            </div>
        );
    }
);

Autocomplete.displayName = 'Autocomplete';

Autocomplete.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    /**
     * The HTML type (e.g. text, number, etc.)
     * */
    type: PropTypes.string,
    suffix: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    /**
     * Function used to filter results from `data` on user input with the signature:
     * `(input, data) => filteredData`.
     *
     * Defaults to filtering based on case-insensitive exact
     * prefix.
     */
    filter: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    /**
     * Text displayed at the top of the results dropdown.
     */
    suggestedText: PropTypes.string.isRequired,
    /**
     * Optional text to display when no results are found. May be `false` to hide the
     * dropdown when no results are found.
     */
    noResultsText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    className: PropTypes.string,
    /**
     * Function used to format the value when displayed in the results box.
     */
    displayFn: PropTypes.func,
    size: PropTypes.number
};

const identityFn = v => v;

Autocomplete.defaultProps = {
    label: 'Label Name',
    type: 'text',
    data: [],
    suggestedText: 'Suggested',
    noResultsText: 'No matches',
    displayFn: identityFn,
    filter: filterByCaseInsensitivePrefix
};

const AutocompleteResults = ({results, displayFn, suggestedText, noResultsText, onClick}) => {
    if (results.length === 0 && !noResultsText) return null;

    var resultsList = null;

    if (results.length > 0) {
        resultsList = (
            <ul>
                {results.map((item, i) => (
                    <li key={i} data-testid="result">
                        <button
                            tabIndex="-1"
                            className="px-3 py-1 w-100 text-start"
                            onMouseDown={() => onClick?.(item)}>
                            {displayFn(item)}
                        </button>
                    </li>
                ))}
            </ul>
        );
    } else {
        resultsList = <p className="px-3">{noResultsText}</p>;
    }

    return (
        <div className="position-absolute border-all bg-structure-white w-100 z-index-dropdown">
            <div className="py-2 overflow-auto" style={{maxHeight: 200}}>
                <p className="px-3 py-1 font-label-xs color-content-dark">{suggestedText}</p>
                {resultsList}
            </div>
        </div>
    );
};

AutocompleteResults.propTypes = {
    displayFn: PropTypes.func,
    noResultsText: PropTypes.any,
    onClick: PropTypes.func,
    results: PropTypes.arrayOf(PropTypes.any),
    suggestedText: PropTypes.any
};

export function filterByCaseInsensitivePrefix(query, data) {
    if (!query) return data;
    const q = query.toString().toLowerCase();
    return data.filter(v => q === v.toString().toLowerCase().substring(0, q.length));
}
